import * as CL from '@design-system/component-library';
import { Grid } from '../Grid/Grid';
import { JsonLd } from 'react-schemaorg';
import {
  LiikkuvaWifiPopOverContent,
  ManagedServicePopoverContent,
  itsenainen5GVerkkoContent,
  mobiiliturvaContent,
  reissaajaContent,
  reissunetti1GtContent,
  reissunetti2GtContent,
} from './HorizontalSubscriptionsContents';
import { MobileCarouselWrapper, getItemList, getSubscription, onSalesProductOrder } from './SubscriptionUtils';
import { SiteContext } from '../../public/site/SiteContext';
import { SubscriptionCard } from './SubscriptionCard';
import { deepEqual } from '../../common/utils/objectUtils';
import { handleOrder } from './OrderUtils';
import {
  mBpsMsg,
  mobileBroadbandMsg,
  mobileRouter,
  orderMsg,
  recommendationUsersDevicesMsg,
  standalone5GMsg,
  t,
  uploadSpeedMsg,
} from '../../common/i18n';
import { paths } from '../../common/constants/pathVariables';
import { useContext } from 'react';
import { useLoaderData, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { CommercialProduct } from '../../generated/api/commercialProduct';
import type { ItemList } from 'schema-dts';
import type { MobileBroadbandSubCardProps } from '../SubscriptionCard/SubscriptionCard';
import type { Offer } from '../../generated/api/offer';
import type { OfferProductMapType } from '../OrderSubscriptionSelection/OrderSubscriptionSelectionUtils';
import type { OnlineModel } from '../../generated/api/onlineModel';
import type { OrderMobileBBSubLoaderData, SubscriptionUpdateLoaderData } from '../../common/loaders';
import type { OrderProps, SubscriptionCardWithModel } from './types';
import type { State } from '../../selfservice/common/store';

export interface HorizontalSubscriptionsProps {
  subscriptionCards: MobileBroadbandSubCardProps[];
  publicPages: boolean;
  isChangeOffer?: boolean;
  onlineModels?: OnlineModel[];
  offerAndFeeMap?: OfferProductMapType[];
}

export const HorizontalMobileBroadbandSubscriptions = ({
  subscriptionCards,
  publicPages,
  isChangeOffer = false,
  onlineModels,
  offerAndFeeMap = [],
}: HorizontalSubscriptionsProps) => {
  const { search, pathname } = useLocation();
  const { siteBaseUrl } = useContext(SiteContext);
  const navigate = useNavigate();
  const noeLoaderData = useLoaderData() as OrderMobileBBSubLoaderData;
  const changeOfferLoader = useLoaderData() as SubscriptionUpdateLoaderData;

  const { campaignsFromVoucher = [], campaignContextsFromVoucher = [] } = useSelector((reduxState: State) => {
    const campaigns = reduxState.campaigns;
    return {
      campaignsFromVoucher: campaigns?.campaigns,
      campaignContextsFromVoucher: campaigns?.campaignContexts,
      voucher: campaigns?.voucher,
    };
  }, deepEqual);

  const findSalesProductCard = (cardName: string): SubscriptionCardWithModel | undefined => {
    const mobileCard = subscriptionCards?.find(card => card.cardName === cardName);
    if (!mobileCard) {
      return undefined;
    }
    const onlineModel = onlineModels?.find(model => model.onlineModelCode === mobileCard.salesProduct.onlineModelCode);
    return onlineModel ? { ...mobileCard, onlineModel } : undefined;
  };

  const getCard = (cards: MobileBroadbandSubCardProps[], cardName: string) =>
    publicPages ? findSalesProductCard(cardName) : getSubscription(cards as SubscriptionCardWithModel[], cardName);

  const onOrder = ({ onlineModel, noeOfferCode, quantity, voucherCode, services }: OrderProps) => {
    const additionalOffers = [
      ...(noeLoaderData.mobileBroadbandModel?.offers?.length ? noeLoaderData.mobileBroadbandModel.offers : []),
      ...(noeLoaderData.elisaNettiModel?.offers?.length ? noeLoaderData.elisaNettiModel.offers : []),
    ];

    handleOrder({
      onlineModel,
      navigate,
      search,
      isChangeOffer,
      quantity,
      voucherCode,
      services,
      offerAndFeeMap,
      campaignsFromVoucher,
      campaignContextsFromVoucher,
      noeOfferCode,
      additionalOffers,
      addOnRules: noeLoaderData.addOnRules,
      subscription: changeOfferLoader.subscription,
      confirmationPath: paths.PS_MOBILE_SUBSCRIPTION_UPDATE_CONFIRMATION,
      productInfoPath: paths.PS_BROADBAND_SUBSCRIPTION_PRODUCT_INFO,
    });
  };

  const onOrderClick = publicPages ? onSalesProductOrder : onOrder;
  const liikkuvaWifiListName = 'Liikkuva WiFi';
  const yritysDataListName = 'Yritysdataliittymät 5G+';
  let analyticsIndexLiikkuvaYritysdata = 0;
  let analyticsIndexYritysdata = 0;

  const sharedProps = {
    publicPages,
    isChangeOffer,
    onOrder: onOrderClick,
    subHeader: t.N7KF(mobileBroadbandMsg),
    buttonText: t.AQOL(orderMsg),
    buttonMakesOrder: true,
  };

  // Override card prices from sales products OnlineModels
  if (publicPages && onlineModels) {
    subscriptionCards.forEach(card => {
      const onlineModel = onlineModels.find(
        (om: OnlineModel) => om.onlineModelCode === card.salesProduct.onlineModelCode
      );
      const offer = onlineModel?.offers.find((of: Offer) => of.offerCode === card.salesProduct.offerCode);
      const oneTimePrice = offer?.commercialProducts.reduce(
        (a: number, c: CommercialProduct) => a + (c.oneTimeCharge || 0),
        0
      );
      const monthlyPrice = offer?.commercialProducts.reduce(
        (a: number, c: CommercialProduct) => a + (c.monthlyRecurringCharge || 0),
        0
      );
      if (oneTimePrice && monthlyPrice) {
        card.monthlyPrice = monthlyPrice;
        card.oneTimePrice = oneTimePrice;
      }
    });
  }

  const LiikkuvaSub1000m = () => (
    <SubscriptionCard
      subscriptionCard={getCard(subscriptionCards, 'wifi1000m')}
      {...sharedProps}
      listName={liikkuvaWifiListName}
      analyticsIndex={analyticsIndexLiikkuvaYritysdata++}
      badges={[
        <CL.Badge key="5g" color="5g" text="5G+" />,
        <CL.Badge key="mobiilireititin" color="green-light" text={t.CV2A(mobileRouter)} />,
      ]}
      header="Liikkuva Yritysdata 1000 M"
      description={t.QF0D('For super-fast business use')}
      infoItems={[
        {
          icon: 'mobile-broadband',
          text: t.I00R('Includes a mobile router'),
          popoverContent: <LiikkuvaWifiPopOverContent />,
        },
        { icon: 'network', text: t.XWHX('Managed service'), popoverContent: <ManagedServicePopoverContent /> },
        {
          icon: 'group',
          text: t.SDTV('All internet users and devices'),
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '100')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '80'),
        },
      ]}
      hasQuantity={true}
      additionalFeatures={[
        {
          icon: 'check',
          text: `Reissunetti 2${t.C4YQ('GB')}`,
          popoverContent: reissunetti2GtContent(),
        },
        {
          icon: 'check',
          text: t.ELB0(standalone5GMsg),
          popoverContent: itsenainen5GVerkkoContent(),
        },
        {
          icon: 'check',
          text: 'Elisan Mobiiliturva Yrityksille',
          popoverContent: mobiiliturvaContent(),
        },
      ]}
    />
  );

  const LiikkuvaSub600m = () => (
    <SubscriptionCard
      subscriptionCard={getCard(subscriptionCards, 'wifi600m')}
      {...sharedProps}
      listName={liikkuvaWifiListName}
      analyticsIndex={analyticsIndexLiikkuvaYritysdata++}
      badges={[
        <CL.Badge key="5g" color="5g" text="5G+" />,
        <CL.Badge key="mobiilireititin" color="green-light" text={t.CV2A(mobileRouter)} />,
      ]}
      header="Liikkuva Yritysdata 600 M"
      description={t.NXK9('For fast business use')}
      infoItems={[
        {
          icon: 'mobile-broadband',
          text: t.I00R('Includes a mobile router'),
          popoverContent: <LiikkuvaWifiPopOverContent />,
        },
        { icon: 'network', text: t.XWHX('Managed service'), popoverContent: <ManagedServicePopoverContent /> },
        {
          icon: 'group',
          text: t.SDTL(recommendationUsersDevicesMsg, '4+', '15+'),
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '75')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '66,5'),
        },
      ]}
      hasQuantity={true}
      additionalFeatures={[
        {
          icon: 'check',
          text: `Reissunetti 1-1,5 ${t.C4YQ('GB')}`,
          popoverContent: reissunetti1GtContent(),
        },
        {
          icon: 'check',
          text: t.ELB0(standalone5GMsg),
          popoverContent: itsenainen5GVerkkoContent(),
        },
        {
          icon: 'check',
          text: 'Elisan Mobiiliturva Yrityksille',
          popoverContent: mobiiliturvaContent(),
        },
      ]}
    />
  );

  const LiikkuvaSub300m = () => (
    <SubscriptionCard
      subscriptionCard={getCard(subscriptionCards, 'wifi300m')}
      {...sharedProps}
      listName={liikkuvaWifiListName}
      analyticsIndex={analyticsIndexLiikkuvaYritysdata++}
      badges={[
        <CL.Badge key="5g" color="5g" text="5G+" />,
        <CL.Badge key="mobiilireititin" color="green-light" text={t.CV2A(mobileRouter)} />,
      ]}
      header="Liikkuva Yritysdata 300 M"
      description={t.EX8C('For active internet use')}
      infoItems={[
        {
          icon: 'mobile-broadband',
          text: t.I00R('Includes a mobile router'),
          popoverContent: <LiikkuvaWifiPopOverContent />,
        },
        { icon: 'network', text: t.XWHX('Managed service'), popoverContent: <ManagedServicePopoverContent /> },
        {
          icon: 'group',
          text: t.SDTL(recommendationUsersDevicesMsg, '3-4', '10-15'),
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '50')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '58,5'),
        },
      ]}
      hasQuantity={true}
      additionalFeatures={[
        {
          icon: 'check',
          text: `Reissunetti 1-1,5 ${t.C4YQ('GB')}`,
          popoverContent: reissunetti1GtContent(),
        },
        {
          icon: 'check',
          text: t.ELB0(standalone5GMsg),
          popoverContent: itsenainen5GVerkkoContent(),
        },
        {
          icon: 'check',
          text: 'Elisan Mobiiliturva Yrityksille',
          popoverContent: mobiiliturvaContent(),
        },
      ]}
    />
  );

  const Sub1000m = () => (
    <SubscriptionCard
      subscriptionCard={getCard(subscriptionCards, '1000m')}
      {...sharedProps}
      listName={yritysDataListName}
      analyticsIndex={analyticsIndexYritysdata++}
      badges={[<CL.Badge key="5g" color="5g" text="5G+" />]}
      header="Yritysdata 1000 M"
      description={t.QF0D('For super-fast business use')}
      infoItems={[
        {
          icon: 'group',
          text: t.SDTV('All internet users and devices'),
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '100')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '80'),
        },
      ]}
      hasQuantity={!isChangeOffer}
      additionalFeatures={[
        {
          icon: 'check',
          text: `Reissunetti 2${t.C4YQ('GB')}`,
          popoverContent: reissunetti2GtContent(),
        },
        {
          icon: 'check',
          text: t.ELB0(standalone5GMsg),
          popoverContent: itsenainen5GVerkkoContent(),
        },
        {
          icon: 'check',
          text: 'Elisan Mobiiliturva Yrityksille',
          popoverContent: mobiiliturvaContent(),
        },
      ]}
    />
  );

  const Sub600m = () => (
    <SubscriptionCard
      subscriptionCard={getCard(subscriptionCards, '600m')}
      {...sharedProps}
      listName={yritysDataListName}
      analyticsIndex={analyticsIndexYritysdata++}
      badges={[<CL.Badge key="5g" color="5g" text="5G+" />]}
      header="Yritysdata 600 M"
      description={t.NXK9('For fast business use')}
      infoItems={[
        {
          icon: 'group',
          text: t.SDTL(recommendationUsersDevicesMsg, '4+', '15+'),
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '66,5')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '75'),
        },
      ]}
      hasQuantity={!isChangeOffer}
      additionalFeatures={[
        {
          icon: 'check',
          text: `Reissunetti 1-1,5 ${t.C4YQ('GB')}`,
          popoverContent: reissunetti1GtContent(),
        },
        {
          icon: 'check',
          text: t.ELB0(standalone5GMsg),
          popoverContent: itsenainen5GVerkkoContent(),
        },
        {
          icon: 'check',
          text: 'Elisan Mobiiliturva Yrityksille',
          popoverContent: mobiiliturvaContent(),
        },
      ]}
    />
  );

  const Sub300m = () => (
    <SubscriptionCard
      subscriptionCard={getCard(subscriptionCards, '300m')}
      {...sharedProps}
      listName={yritysDataListName}
      analyticsIndex={analyticsIndexYritysdata++}
      badges={[<CL.Badge key="5g" color="5g" text="5G+" />]}
      header="Yritysdata 300 M"
      description={t.EX8C('For active internet use')}
      infoItems={[
        {
          icon: 'group',
          text: t.SDTL(recommendationUsersDevicesMsg, '3-4', '10-15'),
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '50')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '58,5'),
        },
      ]}
      hasQuantity={!isChangeOffer}
      additionalFeatures={[
        {
          icon: 'check',
          text: `Reissunetti 1-1,5 ${t.C4YQ('GB')}`,
          popoverContent: reissunetti1GtContent(),
        },
        {
          icon: 'check',
          text: t.ELB0(standalone5GMsg),
          popoverContent: itsenainen5GVerkkoContent(),
        },
        {
          icon: 'check',
          text: 'Elisan Mobiiliturva Yrityksille',
          popoverContent: mobiiliturvaContent(),
        },
      ]}
    />
  );

  const Sub100m = () => (
    <SubscriptionCard
      subscriptionCard={getCard(subscriptionCards, '100m')}
      {...sharedProps}
      listName={yritysDataListName}
      analyticsIndex={analyticsIndexYritysdata++}
      badges={[<CL.Badge key="5g" color="5g" text="5G+" />]}
      header="Yritysdata 100 M"
      description={t.SDTM('Smooth everyday use')}
      infoItems={[
        {
          icon: 'group',
          text: t.SDTL(recommendationUsersDevicesMsg, '1-2', '1-4'),
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '50')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '46,5'),
        },
      ]}
      hasQuantity={!isChangeOffer}
      additionalFeatures={[
        {
          icon: 'check',
          text: 'Reissaaja',
          popoverContent: reissaajaContent(),
        },
        {
          icon: 'check',
          text: t.ELB0(standalone5GMsg),
          popoverContent: itsenainen5GVerkkoContent(),
        },
        {
          icon: 'check',
          text: 'Elisan Mobiiliturva Yrityksille',
          popoverContent: mobiiliturvaContent(),
        },
      ]}
    />
  );

  const Sub50m = () => (
    <SubscriptionCard
      subscriptionCard={getCard(subscriptionCards, '50m')}
      {...sharedProps}
      listName={yritysDataListName}
      analyticsIndex={analyticsIndexYritysdata++}
      badges={[]}
      header="Yritysdata 50 M"
      description={t.KVG8('Video calls, cloud services use and light remote working')}
      infoItems={[
        {
          icon: 'group',
          text: t.SDTL(recommendationUsersDevicesMsg, '1-2', '1-4'),
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '25')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '36'),
        },
      ]}
      hasQuantity={!isChangeOffer}
      additionalFeatures={[
        {
          icon: 'check',
          text: 'Reissaaja',
          popoverContent: reissaajaContent(),
        },
        {
          icon: 'check',
          text: 'Elisan Mobiiliturva Yrityksille',
          popoverContent: mobiiliturvaContent(),
        },
      ]}
    />
  );

  const Sub10m = () => (
    <SubscriptionCard
      subscriptionCard={getCard(subscriptionCards, '10m')}
      {...sharedProps}
      listName={yritysDataListName}
      analyticsIndex={analyticsIndexYritysdata++}
      badges={[]}
      header="Yritysdata 10 M"
      description={t.KVG9('Light internet use, reading emails, sending messages')}
      infoItems={[
        {
          icon: 'group',
          text: t.SDTL(recommendationUsersDevicesMsg, '1-2', '1-4'),
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '5')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '24,5'),
        },
      ]}
      hasQuantity={!isChangeOffer}
      additionalFeatures={[
        {
          icon: 'check',
          text: 'Reissaaja',
          popoverContent: reissaajaContent(),
        },
        {
          icon: 'check',
          text: 'Elisan Mobiiliturva Yrityksille',
          popoverContent: mobiiliturvaContent(),
        },
      ]}
    />
  );

  const shownCardList = subscriptionCards.map(subscriptionCard => subscriptionCard.cardName || '');

  return (
    <Grid colWidthL={publicPages ? 10 : 12}>
      <div className="of-subscriptions-main-container">
        {!isChangeOffer && (
          <>
            <h2 className="of-subscriptions-main-container-carousel-title">Liikkuva yritysdata</h2>
            <MobileCarouselWrapper>
              <LiikkuvaSub1000m />
              <LiikkuvaSub600m />
              <LiikkuvaSub300m />
            </MobileCarouselWrapper>
          </>
        )}
        <h2 className="of-subscriptions-main-container-carousel-title">Yritysdata</h2>
        <MobileCarouselWrapper>
          {shownCardList.includes('1000m') && <Sub1000m />}
          {shownCardList.includes('600m') && <Sub600m />}
          {shownCardList.includes('300m') && <Sub300m />}
          {shownCardList.includes('100m') && <Sub100m />}
          {shownCardList.includes('50m') && <Sub50m />}
          {shownCardList.includes('10m') && <Sub10m />}
        </MobileCarouselWrapper>
        <JsonLd<ItemList> item={getItemList(subscriptionCards as SubscriptionCardWithModel[], siteBaseUrl, pathname)} />
      </div>
    </Grid>
  );
};
